import { EventMarker, EventWithRels } from '@statsbomb/parachute-types';
import { MarkerEvent, ArrowEvent, EventSuccessFail, EventDescription } from '@/types/event';
import { SuspenseAtomResult } from '@/types/jotai';
import { LinkType } from '@/types/link';
import { ShotMapEventMarker } from '@/types/visualisation';
import { getVideoIdsFromEvent } from '@/utils/video';
import { getCornerCurveDirection } from '../cornerCurve';
import { addLinks, flattenAndAddPrefix } from '../object';
import { isNullish } from '../predicates';
import { objSnakeToCamel } from '../queries';

export const processEventDataAtom = (rawEventData: SuspenseAtomResult<EventWithRels[]>) =>
  (rawEventData.data || []).map(data => {
    const flattenedEventData = flattenAndAddPrefix(objSnakeToCamel(data), '.', false);

    const linkFields: { label: string; id: number; type: LinkType }[] = [
      { label: data.player.display_name, id: data.player.player_id, type: 'playerLink' },
      { label: data.team.name, id: data.team.team_id, type: 'teamLink' },
    ];

    const recipientLinkFields: { label: string; id: number; type: LinkType }[] = data.recipient_player
      ? [
          {
            label: data.recipient_player.display_name,
            id: data.recipient_player.player_id,
            type: 'recipientPlayerLink',
          },
        ]
      : [];

    return addLinks(flattenedEventData, [...linkFields, ...recipientLinkFields]);
  });

// https://www.notion.so/statsbomb/Colouring-by-Success-in-Parachute-7f894547ec224e8c843538a53dfb8b99?pvs=4#20edc6bc1f9a43d8b1272429b61f926e
export const getSuccessFail = (type: EventMarker['type'], outcome: EventMarker['outcome']): EventSuccessFail => {
  if (type !== 'pass' || outcome === null) {
    return 'neutral';
  }

  return outcome === 'complete' ? 'success' : 'fail';
};

export const convertMarkerDataIntoMarkers = (eventMarkersData: EventMarker[] = []) =>
  eventMarkersData.reduce(
    (acc, event) => {
      const { markerEvents, arrowEvents } = acc;
      if (isNullish(event.start_x) || isNullish(event.start_y)) return acc;

      const baseEvent = {
        eventId: event.event_id,
        videoEventId: event.video_event_id || undefined,
        startX: event.start_x,
        startY: event.start_y,
        successFail: getSuccessFail(event.type, event.outcome),
        xg: event.xg,
        obv: event.obv,
        gkSaveDifficultyXg: event.gk_save_difficulty_xg,
        gameId: event.game_id,
        period: event.period,
        videoStatus: event.video_status,
        type: event.type,
        outcome: event.outcome,
        penalty: event.penalty,
        freeKick: event.free_kick,
        bodyPart: event.body_part,
        followsDribble: event.follows_dribble,
        followsThroughBall: event.follows_through_ball,
      };

      return !isNullish(event.end_x) && !isNullish(event.end_y)
        ? {
            markerEvents,
            arrowEvents: [
              ...arrowEvents,
              {
                ...baseEvent,
                endX: event.end_x,
                endY: event.end_y,
                curveDirection: event.corner_technique
                  ? getCornerCurveDirection(event.start_x, event.start_y, event.corner_technique)
                  : undefined,
              },
            ],
          }
        : { markerEvents: [...markerEvents, baseEvent], arrowEvents };
    },
    {
      markerEvents: <MarkerEvent[]>[],
      arrowEvents: <ArrowEvent[]>[],
    },
  );

export const convertMarkerDataIntoDescriptions = (eventMarkersData: EventMarker[] = []): EventDescription[] =>
  eventMarkersData.map(event => ({
    eventId: event.event_id,
    videoEventId: event.video_event_id,
    type: event.type,
    minute: event.minute ?? 0,
    second: event.second ?? 0,
    period: event.period,
    playerDisplayName: event.player_display_name,
    gameName: event.game_name,
    competitionName: event.competition_name,
    seasonName: event.season_name,
    gameDate: event.game_date,
    gameId: event.game_id,
    videoStatus: event.video_status,
  }));

export const convertEventDataIntoDescriptions = (eventData: EventWithRels[] = []): EventDescription[] =>
  eventData.map(({ player, event, game, season, competition, user_game: { video_status: videoStatus } }) => ({
    ...getVideoIdsFromEvent(event),
    type: event.type,
    minute: event.attributes.minute ?? 0,
    second: event.attributes.second ?? 0,
    playerDisplayName: player.display_name,
    gameName: game.name,
    competitionName: competition.name,
    seasonName: season.name,
    gameDate: game.date,
    gameId: game.game_id,
    period: event.period,
    videoStatus,
  }));

export const convertMarkerDataToShotMapMarkers = (rawMarkers: EventMarker[]) =>
  rawMarkers
    .filter(
      (marker): marker is EventMarker & { start_x: number; start_y: number } =>
        !isNullish(marker.start_x) && !isNullish(marker.start_y),
    )
    .map(
      (marker): ShotMapEventMarker => ({
        type: marker.type,
        eventId: marker.event_id,
        outcome: marker.outcome,
        xg: marker.xg,
        obv: marker.obv,
        penalty: marker.penalty,
        freeKick: marker.free_kick,
        bodyPart: marker.body_part,
        startX: marker.start_x,
        startY: marker.start_y,
        followsDribble: marker.follows_dribble,
        followsThroughBall: marker.follows_through_ball,
      }),
    );
